import type { AddressState } from '@paid-ui/enums/address';
import type { Address } from '@paid-ui/schemas/zod/address';

import { getGeocode } from 'use-places-autocomplete';

import { defaultAddress } from '@paid-ui/constants';

/**
 * Decode a string address into an address object.
 *
 * @param address - Address string.
 * @returns Address object.
 */
export async function decodeAddress(address?: string): Promise<Address> {
  const addressObject: Address = { ...defaultAddress };

  if (!address) {
    return addressObject;
  }

  try {
    const geoCode = await getGeocode({ address });

    for (const component of geoCode[0]?.address_components ?? []) {
      switch (component.types[0]) {
        case 'country': {
          addressObject.country = component.long_name;
          break;
        }

        case 'postal_code': {
          addressObject.postcode = component.short_name;
          break;
        }

        case 'administrative_area_level_1': {
          addressObject.state = component.short_name as AddressState;
          break;
        }

        case 'colloquial_area':
        case 'locality': {
          addressObject.suburb = component.long_name;
          break;
        }

        case 'route': {
          addressObject.streetName = component.long_name;
          break;
        }

        case 'street_number': {
          addressObject.streetNumber = component.short_name;
          break;
        }

        case 'subpremise': {
          addressObject.unitNumber = component.long_name.replaceAll(/[Uu]nit\s/g, '');
          break;
        }

        default: {
          break;
        }
      }
    }

    return addressObject;
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Decode address failed.');
  }
}
